import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Search } from 'lucide-react';
import { LEVEL_LABELS, LIBRARY_CATEGORIES, LIBRARY_LEVELS, type Drill, type LibraryCategory, type LibraryDrill, type LibraryLevel } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { filterLibrary, useLibrary } from '../../lib/library';
import { Alert, Button, Dialog, Empty, LoadingBlock } from '../ui';
import { CourtSvg } from '../diagram/Court';
import { DiagramView } from '../diagram/DiagramView';

/** Browse the Hoopify drill library: filter, read, copy to your own library. */
export function LibraryBrowser() {
  const library = useLibrary();
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<LibraryCategory | null>(null);
  const [level, setLevel] = useState<LibraryLevel | null>(null);
  const [players, setPlayers] = useState('');
  const [open, setOpen] = useState<LibraryDrill | null>(null);

  if (library.isLoading) return <LoadingBlock />;
  if (library.isError) return <Alert>{errorMessage(library.error)}</Alert>;
  const all = library.data ?? [];
  const list = filterLibrary(all, { q, category, level, players: players ? Number(players) : null });

  return (
    <>
      <p className="mb-4 max-w-[70ch] text-muted">
        {all.length} drills written for Hoopify: court skills from warm-ups to team offence, conditioning, plyometrics, strength and mobility, and drills players can do on their own at home. Add them to a session from the plan’s drill panel, or copy one into your library to make it your own.
      </p>
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="relative min-w-[200px] flex-1 sm:max-w-[300px]">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-faint" aria-hidden />
          <input className="field-input pl-8" placeholder="Search drills" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the Hoopify library" />
        </div>
        <label className="space-y-1">
          <span className="block text-[13px] font-medium">Age</span>
          <select className="field-input py-1.5" value={level ?? ''} onChange={(e) => setLevel((e.target.value || null) as LibraryLevel | null)}>
            <option value="">Any age</option>
            {LIBRARY_LEVELS.filter((l) => l !== 'all').map((l) => (
              <option key={l} value={l}>
                {LEVEL_LABELS[l]}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1">
          <span className="block text-[13px] font-medium">Players I have</span>
          <input className="field-input tnum w-24 py-1.5" type="number" min={1} max={60} value={players} onChange={(e) => setPlayers(e.target.value)} placeholder="Any" />
        </label>
      </div>
      <div className="mb-5 flex flex-wrap gap-1.5" aria-label="Skill">
        {LIBRARY_CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(category === c ? null : c)}
            aria-pressed={category === c}
            className={`rounded-full border px-2.5 py-0.5 text-[13px] ${category === c ? 'border-action bg-action text-action-ink' : 'border-line-strong text-muted hover:text-ink'}`}
          >
            {c}
          </button>
        ))}
      </div>

      {!list.length ? (
        <Empty title="No drills match">Try another skill, or clear the age and player filters.</Empty>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((d) => (
            <li key={d.id}>
              <button onClick={() => setOpen(d)} className="flex h-full w-full gap-3 rounded-lg border border-line bg-surface p-3 text-left hover:border-action/60">
                <div className="w-[72px] shrink-0">
                  {d.diagram ? (
                    <CourtSvg court={d.diagram.court} objects={d.diagram.steps[0]!.objects} className="w-full rounded" label={`Diagram for ${d.title}`} />
                  ) : (
                    <div className="grid aspect-square w-full place-items-center rounded bg-sunken text-center text-[11px] leading-tight text-muted">{d.category}</div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="leading-snug font-medium">{d.title}</p>
                  <p className="mt-0.5 line-clamp-2 text-[13px] text-muted">{d.summary}</p>
                  <p className="tnum mt-1.5 text-[12px] text-muted">
                    {d.minutes} min, {d.category}
                    {d.minPlayers && d.minPlayers > 1 ? `, ${d.minPlayers}+ players` : ''}
                    {d.level !== 'all' ? `, ${LEVEL_LABELS[d.level]}` : ''}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && <LibraryDrillDialog drill={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function LibraryDrillDialog({ drill: d, onClose }: { drill: LibraryDrill; onClose: () => void }) {
  const qc = useQueryClient();
  const copy = useMutation({
    mutationFn: () => api.post<Drill>(`/library/drills/${d.id}/copy`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drills'] }),
  });
  const facts: [string, string | null][] = [
    ['How it runs', d.description],
    ['Coaching points', d.coachingPoints],
    ['Make it easier or harder', d.variations],
    ['Equipment', d.equipment],
    ['Players', d.minPlayers ? (d.maxPlayers ? `${d.minPlayers} to ${d.maxPlayers}` : `${d.minPlayers} or more`) : null],
  ];
  return (
    <Dialog open onClose={onClose} title={d.title} size={d.diagram ? 'lg' : 'md'}>
      <div className="max-h-[65dvh] space-y-3 overflow-y-auto pr-1">
        <p className="tnum text-[14px] text-muted">
          {d.minutes} min, {d.category}, {LEVEL_LABELS[d.level]}
        </p>
        {d.diagram && <DiagramView diagram={d.diagram} />}
        {facts
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k}>
              <p className="text-[12px] font-semibold text-muted">{k}</p>
              <p className="whitespace-pre-line">{v}</p>
            </div>
          ))}
      </div>
      {copy.isError && <div className="mt-4"><Alert>{errorMessage(copy.error)}</Alert></div>}
      <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
        {copy.isSuccess ? (
          <>
            <span className="mr-auto inline-flex items-center gap-1.5 text-[14px] text-ok">
              <Check className="size-4" aria-hidden /> In your library now
            </span>
            <Link to="/drills" onClick={onClose} className="btn-secondary">
              See my drills
            </Link>
          </>
        ) : (
          <>
            <span className="mr-auto text-[13px] text-muted">To use it as is, add it from a session’s drill panel (Hoopify tab).</span>
            <Button loading={copy.isPending} onClick={() => copy.mutate()}>
              <Copy className="size-4" aria-hidden /> Copy to my library
            </Button>
          </>
        )}
      </div>
    </Dialog>
  );
}
